import { useEffect, useRef, useState } from "react";
import { apiClient } from "@/App";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin } from "lucide-react";

const newSession = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()));

// Text field with address suggestions. Typing only calls onChange; picking a suggestion calls onSelect with coordinates.
export const AddressAutocomplete = ({ value, onChange, onSelect, placeholder, testId, className = "", rightSlot = null }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const typed = useRef(false);
  const session = useRef(newSession());
  const reqId = useRef(0);
  const inputRef = useRef(null);
  const focused = () => document.activeElement === inputRef.current;

  useEffect(() => {
    if (!typed.current || (value || "").trim().length < 3) {
      setItems([]);
      return undefined;
    }
    const id = ++reqId.current;
    const t = setTimeout(async () => {
      if (!focused()) return;
      setLoading(true);
      try {
        const { data } = await apiClient.get("/places/autocomplete", { params: { q: value, session: session.current } });
        if (id === reqId.current) {
          setItems(data.suggestions || []);
          setOpen(focused());
          setActive(-1);
        }
      } catch {
        if (id === reqId.current) setItems([]);
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [value]);

  const choose = async (s) => {
    typed.current = false;
    setOpen(false);
    setItems([]);
    let place = { address: s.description, latitude: s.latitude, longitude: s.longitude };
    if (s.latitude == null) {
      try {
        const { data } = await apiClient.get("/places/details", { params: { place_id: s.id, session: session.current } });
        place = { address: data.address || s.description, latitude: data.latitude, longitude: data.longitude };
      } catch {
        place = { address: s.description, latitude: 0, longitude: 0 };
      }
    }
    session.current = newSession();
    onSelect(place);
  };

  const onKeyDown = (e) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % items.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a <= 0 ? items.length - 1 : a - 1)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); choose(items[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative flex-1 min-w-0">
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => { typed.current = true; onChange(e.target.value); }}
        onKeyDown={onKeyDown}
        onFocus={() => items.length && setOpen(true)}
        onBlur={() => setTimeout(() => { reqId.current += 1; setLoading(false); setOpen(false); }, 150)}
        placeholder={placeholder}
        autoComplete="off"
        className={`h-12 text-lg ${rightSlot ? "pr-20" : "pr-9"} ${className}`}
        data-testid={testId}
      />
      {loading && <Loader2 className={`w-4 h-4 animate-spin text-slate-400 absolute top-4 ${rightSlot ? "right-12" : "right-3"}`} />}
      {rightSlot && <div className="absolute right-2 top-2">{rightSlot}</div>}
      {open && items.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden" data-testid={`${testId}-suggestions`}>
          {items.map((s, i) => (
            <li key={s.id}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); choose(s); }}
                className={`w-full text-left px-3 py-2.5 flex items-start gap-2 transition-colors duration-100 ${i === active ? "bg-slate-100" : "hover:bg-slate-50"}`}
                data-testid={`${testId}-suggestion-${i}`}
              >
                <MapPin className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-900 truncate">{s.main || s.description}</span>
                  {s.secondary && <span className="block text-xs text-slate-500 truncate">{s.secondary}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressAutocomplete;
